<template>
  <article id="lodomelix-content" class="lm-page" tabindex="-1">
    <section class="lm-hero lm-section">
      <div class="container-wrapper lm-split">
        <div>
          <p class="lm-eyebrow">ARI GAMES / FANTASY HERO RPG</p>
          <h1>LODOMELIX</h1>
          <p class="lm-tagline">
            Build your hero.<br />Take on the next challenge.
          </p>
          <p class="lm-lead">
            Collect heroes, equip your strongest gear and turn hard-won rewards
            into your next upgrade. From auto-battles to tower bosses, every
            encounter starts with the hero you build.
          </p>
          <div class="lm-actions">
            <v-btn color="primary" rounded depressed large href="#gameplay"
              >Explore the game</v-btn
            ><v-btn outlined rounded large href="#heroes"
              >Meet the heroes</v-btn
            >
          </div>
          <p><a href="#join-test">Help shape LODOMELIX — join the test</a></p>
          <p class="lm-note">
            <span class="lm-status">In testing</span> Android · Store audience
            13+<br />Public release date unconfirmed. No public download is
            available here.
          </p>
        </div>
        <figure class="lm-hero-art">
          <picture
            ><source
              type="image/webp"
              :srcset="art + 'hero-small.webp 720w, ' + art + 'hero.webp 1200w'"
              sizes="(max-width: 760px) 100vw, 50vw" />
            <img
              :src="art + 'hero.jpg'"
              width="1200"
              height="1000"
              alt="Kael in gold armour, Frost Warden with an ice shield and Aurelion holding an arcane staff"
              fetchpriority="high"
          /></picture>
          <figcaption>
            Promotional composition using original game artwork.
          </figcaption>
        </figure>
      </div>
    </section>
    <section id="gameplay" class="lm-section lm-tint">
      <div class="container-wrapper">
        <p class="lm-eyebrow">THE GAME LOOP</p>
        <h2>Fight. Refine. Grow stronger.</h2>
        <p class="lm-intro">
          Choose an active hero, prepare their build and return from battle with
          resources to invest in your collection.
        </p>
        <div class="lm-grid-three">
          <article v-for="(item, i) in loop" :key="item.title" class="lm-card">
            <span class="lm-number">0{{ i + 1 }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.body }}</p>
          </article>
        </div>
      </div>
    </section>
    <LodomelixCollection />
    <LodomelixCollection is-weapons />
    <section id="screenshots" class="lm-section lm-dark">
      <div class="container-wrapper">
        <p class="lm-eyebrow">INSIDE LODOMELIX</p>
        <h2>A look at the real game.</h2>
        <p class="lm-intro">
          Actual Android release-candidate captures, with progress earned
          through play. These screens also feature in the 1.0.0 build. Open an
          image to inspect the full interface.
        </p>
        <div class="lm-grid-three lm-screens">
          <figure v-for="shot in shots" :key="shot.image">
            <button
              type="button"
              :aria-label="'Enlarge ' + shot.title + ' screenshot'"
              @click="
                selectedShot = shot;
                galleryOpen = true;
              "
            >
              <picture
                ><source
                  type="image/webp"
                  :srcset="
                    art +
                    shot.image +
                    '-small.webp 540w, ' +
                    art +
                    shot.image +
                    '.webp 1080w'
                  "
                  sizes="(max-width: 600px) 86vw, 33vw" />
                <img
                  :src="art + shot.image + '.jpg'"
                  width="1080"
                  height="1920"
                  loading="lazy"
                  :alt="shot.alt"
              /></picture>
            </button>
            <figcaption>
              <h3>{{ shot.title }}</h3>
              <p>{{ shot.body }}</p>
            </figcaption>
          </figure>
        </div>
        <p class="lm-note">
          The Royal Arena uses simulated opponents. This build does not offer
          live multiplayer matches.
        </p>
      </div>
    </section>
    <section class="lm-section">
      <div class="container-wrapper lm-split">
        <div>
          <p class="lm-eyebrow">MAKE YOUR NEXT UPGRADE COUNT</p>
          <h2>More than a higher level.</h2>
          <p class="lm-lead">
            Match weapons and armour to your hero, strengthen skills through
            training, and work towards evolution. Gear, wings and pets all
            contribute to the build.
          </p>
          <div
            class="lm-equipment"
            aria-label="Original equipment artwork from the game"
          >
            <figure v-for="item in equipment" :key="item.image">
              <picture
                ><source
                  type="image/webp"
                  :srcset="art + item.image + '.webp'" />
                <img
                  :src="art + item.image + '.png'"
                  :alt="item.name + ' artwork from LODOMELIX'"
                  width="256"
                  height="256"
                  loading="lazy"
              /></picture>
              <figcaption>{{ item.name }}</figcaption>
            </figure>
          </div>
        </div>
        <div class="lm-card">
          <h3>Earn it through play.</h3>
          <p>
            Battle rewards include experience, gold and items. Use earned
            currencies for heroes, equipment and shop supplies. Evolution asks
            for resources and progression milestones.
          </p>
          <p>
            <strong>The 1.0.0 build has no ads or real-money purchases.</strong>
            Availability and future versions may change; this page describes the
            build currently in testing.
          </p>
        </div>
      </div>
    </section>
    <LodomelixTestRegistration />
    <section id="privacy-account" class="lm-section lm-tint">
      <div class="container-wrapper lm-split">
        <div>
          <p class="lm-eyebrow">PRIVACY &amp; ACCOUNT</p>
          <h2>Your progress.<br />Your account controls.</h2>
          <p>
            Sign in with email and password or Google. Your account links local
            progress with a Firebase cloud save, including your heroes,
            inventory and battle progression.
          </p>
          <p>
            Deleting an account removes its access and saved progress. Some
            security records remain.<span v-if="reviewDrafts"> The deletion page explains what happens.</span> Contact privacy@ari.company for help with your data.
          </p>
        </div>
        <div class="lm-card lm-resource-links">
          <router-link v-if="reviewDrafts" to="/games/ludomelix/privacy-policy"
            >Privacy Policy <span aria-hidden="true">↗</span></router-link
          ><router-link v-if="reviewDrafts" to="/games/ludomelix/delete-account"
            >Delete account and data
            <span aria-hidden="true">↗</span></router-link
          ><router-link v-if="reviewDrafts" to="/games/ludomelix/testing-terms"
            >Testing Terms <span aria-hidden="true">↗</span></router-link
          ><a href="mailto:lodomelix@ari.company"
            >Game support <span aria-hidden="true">↗</span></a
          >
          <p class="lm-note">
            Support: lodomelix@ari.company<br />Privacy: privacy@ari.company<br />Email
            links open your mail application.
          </p>
        </div>
      </div>
    </section>
    <v-dialog
      ref="gallery"
      v-model="galleryOpen"
      max-width="620"
      content-class="lm-image-dialog"
      ><v-card v-if="selectedShot"
        ><v-card-title class="d-flex justify-space-between"
          ><span id="lm-screenshot-title">{{ selectedShot.title }}</span
          ><v-btn
            text
            @click="galleryOpen = false"
            aria-label="Close screenshot"
            >Close</v-btn
          ></v-card-title
        ><v-card-text
          ><img
            :src="art + selectedShot.image + '.jpg'"
            :alt="selectedShot.alt"
            width="1080"
            height="1920"
            style="
              display: block;
              width: 100%;
              height: auto;
            " /></v-card-text></v-card
    ></v-dialog>
  </article>
</template>
<script>
import LodomelixCollection from "./LodomelixCollection.vue";
import LodomelixTestRegistration from "./LodomelixTestRegistration.vue";
import { gameMeta } from "./lodomelix-meta";
export default {
  name: "LudomelixPage",
  components: { LodomelixCollection, LodomelixTestRegistration },
  metaInfo() {
    return gameMeta(
      "LODOMELIX — Fantasy Hero RPG | ARI.company",
      "Collect heroes, build their equipment and skills, and challenge tower bosses in LODOMELIX. Android game in testing from ARI.company.",
    );
  },
  watch: {
    galleryOpen(open) {
      if (open)
        this.$nextTick(() => {
          const dialog = this.$refs.gallery && this.$refs.gallery.$refs.content;
          if (dialog)
            dialog.setAttribute("aria-labelledby", "lm-screenshot-title");
        });
    },
  },
  data: () => ({
    reviewDrafts: process.env.VUE_APP_INCLUDE_REVIEW_DRAFTS === "true",
    art: "/games/ludomelix/release-1/",
    equipment: [
      { name: "Iron Sword", image: "iron-sword" },
      { name: "Wooden Shield", image: "wooden-shield" },
      { name: "Iron Amulet", image: "iron-amulet" },
    ],
    galleryOpen: false,
    selectedShot: null,
    loop: [
      {
        title: "Prepare your hero",
        body: "Choose a hero and equip compatible weapons, armour and accessories. Balance attack, defence and magic for the fight ahead.",
      },
      {
        title: "Enter the battle",
        body: "Follow auto-battles, respond to adventure events and manage recovery. Challenge Abyss Tower bosses or choose a team for the Royal Raid.",
      },
      {
        title: "Invest the rewards",
        body: "Bring experience, currency and loot back to your collection. Train skills, improve gear and pursue your next evolution milestone.",
      },
    ],
    shots: [
      {
        image: "character",
        title: "Character & equipment",
        alt: "Actual Character screen showing Kael at level 10, his equipment view and game navigation",
        body: "Inspect your hero and the build behind their power.",
      },
      {
        image: "tower",
        title: "Abyss Tower",
        alt: "Actual Abyss Tower screen with Kael, the floor-one boss and battle status",
        body: "Defeat the current floor’s boss to unlock the next challenge.",
      },
      {
        image: "raid",
        title: "Royal Raid",
        alt: "Actual Royal Raid battle replay with hero and boss combat information",
        body: "Bring a selected team into a boss encounter and review the result.",
      },
    ],
  }),
};
</script>
<style src="./lodomelix.css"></style>
